import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Dialog, DialogContent, Stack, Typography, useTheme } from '@mui/material';

import { ProcessTalentInvitationResponse } from '@/api/generated';
import CopyIcon from '@/assets/icons/copy.svg?react';
import RectangleAsterisks from '@/assets/icons/rectangle-asterisks.svg?react';
import LoadingButton from '@/components/LoadingButton';
import { useFormattedDatetime } from '@/hooks/useFormattedDatetime';
import palette from '@/theme/palette';

interface GeneratePasswordModalProps {
  open: boolean;
  onClose: () => void;
  invitationResponse?: ProcessTalentInvitationResponse;
  onGenerateAgain: () => void;
  isGenerating: boolean;
}

const GeneratePasswordModal: FC<GeneratePasswordModalProps> = ({
  open,
  onClose,
  invitationResponse,
  onGenerateAgain,
  isGenerating
}) => {
  const { t } = useTranslation();
  const { spacing } = useTheme();
  const { formatDatetime } = useFormattedDatetime();

  const handleCopyCode = useCallback(() => {
    if (invitationResponse?.accessKey) {
      navigator.clipboard.writeText(invitationResponse.accessKey);
    }
  }, [invitationResponse]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent
        sx={{
          width: spacing(58.5),
          p: 0,
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Stack alignItems="center" sx={{ padding: spacing(2) }}>
          <Box component="img" src="/images/tablet-green-key.svg" sx={{ height: spacing(16) }} />
          <Typography variant="h3" sx={{ marginTop: spacing(1) }}>
            {t('processes:layouts.process.menu.generatePassword.passwordTitle')}
          </Typography>

          <Typography variant="body2" color="grey.700" sx={{ marginTop: spacing(1) }}>
            {invitationResponse &&
              t('processes:layouts.process.menu.generatePassword.expiresAt', {
                date: formatDatetime(invitationResponse.expiresAt!)
              })}
          </Typography>
          <Stack sx={{ flexDirection: 'row', gap: spacing(1), marginTop: spacing(2) }}>
            {invitationResponse &&
              invitationResponse.accessKey!.split('').map((char: string, index: number) => (
                <Typography
                  variant="body2"
                  key={`${char}-${index}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: spacing(5),
                    height: spacing(5),
                    border: `1px solid ${palette.grey[600]}`,
                    borderRadius: spacing(0.5)
                  }}
                >
                  {char}
                </Typography>
              ))}
          </Stack>
        </Stack>
        <Stack direction="row" sx={{ padding: spacing(2.5, 2, 2), gap: spacing(1) }}>
          <Button
            variant="text"
            disabled={!invitationResponse?.accessKey}
            startIcon={<CopyIcon />}
            onClick={handleCopyCode}
          >
            {t('common:actions.copy')}
          </Button>
          <LoadingButton
            startIcon={<RectangleAsterisks />}
            onClick={onGenerateAgain}
            variant="text"
            isLoading={isGenerating}
          >
            {t('processes:layouts.process.menu.generatePassword.generateAgain')}
          </LoadingButton>
          <Button variant="contained" onClick={onClose} sx={{ marginInlineStart: 'auto' }}>
            {t('common:actions.close')}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default GeneratePasswordModal;
